export interface AtmosphereCategoryRef {
  categoryId: string;
  code: string;
  name: string;
  category: string;
}

export interface AtmosphereCategoryLookup {
  categoryId: string;
  code: string;
  name: string;
  enabled?: boolean;
}

function normalizeCategoryInput(raw: string): string {
  return raw.trim().replace(/[—–－]/g, "-");
}

function formatCategoryDisplay(code: string, name: string): string {
  return `${code}-${name}`;
}

function toRef(cat: AtmosphereCategoryLookup): AtmosphereCategoryRef {
  return {
    categoryId: cat.categoryId,
    code: cat.code,
    name: cat.name,
    category: formatCategoryDisplay(cat.code, cat.name),
  };
}

function parseCombinedCategory(raw: string): { code?: string; name?: string } {
  const normalized = normalizeCategoryInput(raw);
  const dashMatch = normalized.match(/^(\d+)\s*[-]\s*(.+)$/);
  if (dashMatch) {
    return { code: dashMatch[1], name: dashMatch[2]!.trim() };
  }

  const spaceMatch = normalized.match(/^(\d+)\s+(.+)$/);
  if (spaceMatch) {
    return { code: spaceMatch[1], name: spaceMatch[2]!.trim() };
  }

  return {};
}

export function resolveAtmosphereCategory(
  input: { categoryId?: string; category?: string },
  categories: AtmosphereCategoryLookup[]
): AtmosphereCategoryRef | null {
  const enabledCategories = categories.filter((cat) => cat.enabled !== false);

  if (input.categoryId) {
    const byId = enabledCategories.find((cat) => cat.categoryId === input.categoryId);
    if (byId) return toRef(byId);
  }

  const raw = normalizeCategoryInput(input.category || "");
  if (!raw) return null;

  const byIdFromRaw = enabledCategories.find((cat) => cat.categoryId === raw);
  if (byIdFromRaw) return toRef(byIdFromRaw);

  const byCode = enabledCategories.find((cat) => cat.code === raw);
  if (byCode) return toRef(byCode);

  const byName = enabledCategories.find((cat) => cat.name === raw);
  if (byName) return toRef(byName);

  const { code, name } = parseCombinedCategory(raw);
  if (code && name) {
    const byCodeAndName = enabledCategories.find(
      (cat) => cat.code === code && cat.name === name
    );
    if (byCodeAndName) return toRef(byCodeAndName);
  }

  if (code) {
    const byParsedCode = enabledCategories.find((cat) => cat.code === code);
    if (byParsedCode) return toRef(byParsedCode);
  }

  if (name) {
    const byParsedName = enabledCategories.find((cat) => cat.name === name);
    if (byParsedName) return toRef(byParsedName);
  }

  return null;
}

export const UNMATCHED_ATMOSPHERE_CATEGORY_NAME = "未分类";

export function resolveAtmosphereCategoryOrDefault(
  input: { categoryId?: string; category?: string },
  categories: AtmosphereCategoryLookup[]
): AtmosphereCategoryRef {
  const resolved = resolveAtmosphereCategory(input, categories);
  if (resolved) return resolved;

  const raw = normalizeCategoryInput(input.category || "");
  return {
    categoryId: "",
    code: "",
    name: UNMATCHED_ATMOSPHERE_CATEGORY_NAME,
    category: raw || UNMATCHED_ATMOSPHERE_CATEGORY_NAME,
  };
}

export function formatAtmosphereCategoryLabel(item: {
  categoryCode?: string;
  categoryName?: string;
  category?: string;
}): string {
  if (
    item.categoryName === UNMATCHED_ATMOSPHERE_CATEGORY_NAME ||
    item.categoryName === "未分组"
  ) {
    return UNMATCHED_ATMOSPHERE_CATEGORY_NAME;
  }
  if (item.categoryCode && item.categoryName) {
    return formatCategoryDisplay(item.categoryCode, item.categoryName);
  }
  return item.category || "-";
}
